import { useState } from "react";
import { Avatar, AvatarImage, AvatarFallback } from "./avatar";
import { Tooltip } from "@tellescope/design-system";
import { Popover, PopoverTrigger, PopoverContent } from "./popover";
import { Input } from "./input";
import { Check, UserPlus } from "lucide-react";
import { cn } from "../lib/utils";
import { useTranslation } from "react-i18next";
import { i18n } from "@tellescope/i18n";

export interface AssigneeAdmin {
  id: string;
  name: string;
  email?: string;
  avatar?: string;
}

export interface AssigneeData {
  assignees: string[]; // Array of admin IDs
  primaryAdmin?: string; // ID of the primary admin
}

export interface AssigneeAvatarGroupProps {
  availableAdmins: AssigneeAdmin[];
  assignees?: string[];
  primaryAdmin?: string;
  onAssigneeChange?: (assignees: string[], primaryAdmin?: string) => void;
  className?: string;
  maxVisible?: number;
  /** When true, disables primary admin selection. The left checkbox toggles assignee status instead. */
  disablePrimaryChange?: boolean;
  label?: string;
}

function getInitials(name: string) {
  return name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function PrimaryCheckbox({
  checked,
  onCheckedChange,
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <button
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        "w-4 h-4 rounded border-2 flex items-center justify-center text-xs font-bold transition-colors",
        checked ? "bg-black border-black text-white" : "border-gray-300 hover:border-gray-400"
      )}
    >
      {checked && "P"}
    </button>
  );
}

function AssigneeCheckbox({
  checked,
  onCheckedChange,
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <button
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        "w-4 h-4 rounded border-2 flex items-center justify-center transition-colors",
        checked ? "bg-black border-black text-white" : "border-gray-300 hover:border-gray-400"
      )}
    >
      {checked && <Check className="w-3 h-3" />}
    </button>
  );
}

export function AssigneeAvatarGroup({
  availableAdmins,
  assignees = [],
  primaryAdmin,
  onAssigneeChange,
  className,
  maxVisible = 2,
  disablePrimaryChange = false,
  label = i18n.t("Assign care team members", { ns: "ui" }),
}: AssigneeAvatarGroupProps) {
  const { t } = useTranslation("ui");
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Get assigned admins, with primary admin first
  const assignedAdmins = availableAdmins.filter((admin) => assignees.includes(admin.id));
  const sortedAssignedAdmins = assignedAdmins.sort((a, b) => {
    // Primary admin always comes first
    if (primaryAdmin === a.id) return -1;
    if (primaryAdmin === b.id) return 1;
    return 0;
  });

  // Determine display avatars
  const visibleAvatars = sortedAssignedAdmins.slice(0, maxVisible);
  const remainingCount = Math.max(0, sortedAssignedAdmins.length - maxVisible);

  // Filter admins for menu based on search
  const filteredAdmins = availableAdmins.filter(
    (admin) =>
      admin.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      admin.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handlePrimaryAdminChange = (adminId: string, isPrimary: boolean) => {
    if (isPrimary) {
      // Set as primary admin and ensure they're also an assignee
      const newAssignees = assignees.includes(adminId) ? assignees : [...assignees, adminId];
      onAssigneeChange?.(newAssignees, adminId);
    } else if (primaryAdmin === adminId) {
      // Remove as primary admin
      onAssigneeChange?.(assignees, undefined);
    }
  };

  const handleAssigneeToggle = (adminId: string) => {
    const isCurrentlyAssigned = assignees.includes(adminId);
    if (isCurrentlyAssigned) {
      // Remove as assignee
      const newAssignees = assignees.filter((id) => id !== adminId);
      const newPrimaryAdmin = primaryAdmin === adminId ? undefined : primaryAdmin;
      onAssigneeChange?.(newAssignees, newPrimaryAdmin);
    } else {
      // Add as assignee
      const newAssignees = [...assignees, adminId];
      onAssigneeChange?.(newAssignees, primaryAdmin);
    }
  };

  if (assignedAdmins.length === 0) {
    return (
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <div
            className={cn(
              "w-8 h-8 hover:bg-muted transition-colors rounded-full flex items-center justify-center cursor-pointer",
              className
            )}
          >
            <UserPlus className="w-4 h-4 text-muted-foreground" />
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="end">
          <div className="p-3 border-b space-y-2">
            <h4 className="text-sm font-medium text-foreground">{label}</h4>
            <Input
              placeholder={t("Search users...")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-8"
            />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {filteredAdmins.map((admin) => (
              <div key={admin.id} className="flex items-center gap-3 p-3 hover:bg-muted/50">
                {disablePrimaryChange ? (
                  <AssigneeCheckbox
                    checked={assignees.includes(admin.id)}
                    onCheckedChange={() => handleAssigneeToggle(admin.id)}
                  />
                ) : (
                  <PrimaryCheckbox
                    checked={primaryAdmin === admin.id}
                    onCheckedChange={(checked) => handlePrimaryAdminChange(admin.id, checked)}
                  />
                )}
                <Avatar className="w-8 h-8">
                  <AvatarImage src={admin.avatar} alt={admin.name} />
                  <AvatarFallback className="text-xs" useStaticBackground={true} id={admin.id}>
                    {getInitials(admin.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => handleAssigneeToggle(admin.id)}>
                  <p className="text-sm font-medium truncate">{admin.name}</p>
                  {admin.email && <p className="text-xs text-muted-foreground truncate">{admin.email}</p>}
                </div>
                {!disablePrimaryChange && assignees.includes(admin.id) && <Check className="w-4 h-4 text-black" />}
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div className={cn("flex items-center -space-x-2 cursor-pointer", className)}>
          {visibleAvatars.map((admin, index) => (
            <Tooltip key={admin.id}>
              <Tooltip.Trigger asChild>
                <div className="relative" style={{ zIndex: visibleAvatars.length - index }}>
                  <Avatar className="w-8 h-8 border-2 border-background">
                    <AvatarImage src={admin.avatar} alt={admin.name} />
                    <AvatarFallback className="text-sm" useStaticBackground={true} id={admin.id}>
                      {getInitials(admin.name)}
                    </AvatarFallback>
                  </Avatar>
                  {primaryAdmin === admin.id && (
                    <div className="absolute -top-1 -left-1 w-4 h-4 bg-black border-2 border-white rounded-full flex items-center justify-center z-10">
                      <span className="text-white text-[10px] font-bold leading-none">P</span>
                    </div>
                  )}
                </div>
              </Tooltip.Trigger>
              <Tooltip.Content>
                <p>
                  {admin.name}
                  {primaryAdmin === admin.id ? " (Primary)" : ""}
                </p>
              </Tooltip.Content>
            </Tooltip>
          ))}

          {remainingCount > 0 && (
            <Tooltip>
              <Tooltip.Trigger asChild>
                <div
                  className="relative w-8 h-8 rounded-full bg-muted border-2 border-background flex items-center justify-center"
                  style={{ zIndex: 1 }}
                >
                  <span className="text-sm text-muted-foreground">+{remainingCount}</span>
                </div>
              </Tooltip.Trigger>
              <Tooltip.Content>
                <p>{t("{{count}} more assignees", { count: remainingCount })}</p>
              </Tooltip.Content>
            </Tooltip>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="p-3 border-b space-y-2">
          <h4 className="text-sm font-medium text-foreground">{label}</h4>
          <Input
            placeholder={t("Search users...")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8"
          />
        </div>
        <div className="max-h-64 overflow-y-auto">
          {filteredAdmins.map((admin) => (
            <div key={admin.id} className="flex items-center gap-3 p-3 hover:bg-muted/50">
              {disablePrimaryChange ? (
                <AssigneeCheckbox
                  checked={assignees.includes(admin.id)}
                  onCheckedChange={() => handleAssigneeToggle(admin.id)}
                />
              ) : (
                <PrimaryCheckbox
                  checked={primaryAdmin === admin.id}
                  onCheckedChange={(checked) => handlePrimaryAdminChange(admin.id, checked)}
                />
              )}
              <Avatar className="w-8 h-8">
                <AvatarImage src={admin.avatar} alt={admin.name} />
                <AvatarFallback className="text-xs">{getInitials(admin.name)}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0 cursor-pointer" onClick={() => handleAssigneeToggle(admin.id)}>
                <p className="text-sm font-medium truncate">{admin.name}</p>
                {admin.email && <p className="text-xs text-muted-foreground truncate">{admin.email}</p>}
              </div>
              {!disablePrimaryChange && assignees.includes(admin.id) && <Check className="w-4 h-4 text-black" />}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
